import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getSharedResults } from "../pronunciation";
import { useTranslation } from "react-i18next";

export default function ShareLanding() {
    const { t } = useTranslation();
    const { shareCode } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null); // { language, sentence, recognized_sentence?, scores? }
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                setNotFound(false);
                const res = await getSharedResults(shareCode);

                console.log(res);

                if (!cancelled) setData(res);
            } catch (error) {
                console.error("Error fetching shared results:", error);
                if (!cancelled) {
                    setNotFound(true);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [shareCode]);

    const handleTryIt = () => {
        const prefillLanguage = data?.language || "";
        const prefillText = data?.sentence || "";
        const params = new URLSearchParams();
        if (prefillLanguage) params.append("lang", prefillLanguage);
        if (prefillText) params.append("text", prefillText);
        navigate(`/?${params.toString()}`);
    };

    return (
        <>
            <div className="mx-auto max-w-2xl p-4 sm:p-6">
                {/* Heading */}
                <h1 className="text-2xl font-semibold tracking-tight mb-4">{t("shared-title")}</h1>

                {/* Loading state */}
                {loading && (
                    <div className="animate-pulse rounded-xl border border-border bg-secondary p-6">
                        <div className="h-5 w-24 rounded bg-primary/30 mb-4" />
                        <div className="h-4 w-3/4 rounded bg-primary/20 mb-2" />
                        <div className="h-4 w-2/3 rounded bg-primary/20" />
                    </div>
                )}

                {/* Not found state */}
                {!loading && notFound && (
                    <div className="rounded-xl border border-red-200 p-6">
                        <p className="mb-4 text-sm text-red-700">{t("shared-not-found")}</p>
                        <button
                            onClick={() => navigate("/")}
                            className="inline-flex items-center rounded-xl px-4 py-2 text-sm font-medium border border-border bg-white hover:bg-gray-50"
                        >
                            {t("shared-return-to-home")}
                        </button>
                    </div>
                )}

                {/* Success state */}
                {!loading && !notFound && (
                    <div>
                        <div className="rounded-xl border border-border bg-secondary p-4 sm:p-6">
                            {/* Language badge */}
                            <div className="mb-3 inline-flex items-center rounded-lg border border-border bg-background px-2.5 py-1 text-xs font-medium">
                                {data?.language === "en-GB"
                                    ? t("English")
                                    : data?.language === "fr-FR"
                                    ? t("French")
                                    : data?.language === "de-DE"
                                    ? t("German")
                                    : t("Unknown language")}
                            </div>

                            {/* Sentence */}
                            <p className="text-lg leading-relaxed mb-4 font-semibold">
                                {data?.sentence || "—"}
                            </p>

                            {/* Optional recognized sentence */}
                            {data?.recognized_sentence && (
                                <div className="mt-2 text-sm">
                                    {t("shared-recognized-sentence")} <br></br>
                                    <span className="font-medium text-base">
                                        {data.recognized_sentence}
                                    </span>
                                </div>
                            )}

                            {/* Optional score bar (if you returned scores) */}
                            {data?.scores && (
                                <div className="mt-4 flex h-8 text-xs font-medium text-white gap-px bg-purple-600 rounded-lg overflow-hidden">
                                    <div className="bg-primary flex-1 flex items-center justify-center">
                                        {t("scores.accuracy") +
                                            ": " +
                                            Math.ceil(data.scores.accuracy)}
                                    </div>
                                    <div className="bg-primary flex-1 flex items-center justify-center">
                                        {t("scores.completeness") +
                                            ": " +
                                            Math.ceil(data.scores.completeness)}
                                    </div>
                                    <div className="bg-primary flex-1 flex items-center justify-center">
                                        {t("scores.fluency") +
                                            ": " +
                                            Math.ceil(data.scores.fluency)}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="mt-4 w-full">
                            <button
                                onClick={handleTryIt}
                                className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-purple-600"
                            >
                                {t("shared-try-it-yourself")}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
